/**
 * Phase R — Per-section clone of `pages/WMS/SFGBinToBinTransfer.jsx` (FULL Salem parity).
 *
 * MACHINE-GENERATED by `scripts/build_per_section_wms_setup_frontend.py`.
 * Mounted at `/sections/dispatch/:section/wms-setup/<flow>`. All `${apiBase}/...`
 * API calls are rewritten to `/per-section/${slug}/wms-setup/...`.
 *
 * DO NOT hand-edit. Edit Salem's source of truth and re-run the transformer.
 */
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Package, ArrowRightLeft, Check, RefreshCw, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import BarcodeInput from '@/components/BarcodeInput';
import ScanFeedbackOverlay from '@/components/ScanFeedbackOverlay';
import apiClient from '@/services/apiClient';

const SLUG_TO_DISPLAY = {
  color_combo: 'Color Combo',
  art_silk: 'Art Silk',
  kids: 'Kids',
};

// PHASE_R_FULL_CLONE = true (sentinel for tests)

const PerSectionSFGBinToBinTransfer = () => {
  const navigate = useNavigate();
  const { section: slug } = useParams();
  const sectionName = SLUG_TO_DISPLAY[slug];
  const basePath = `/sections/dispatch/${slug}`;
  const apiBase = `/per-section/${slug}/wms-setup`;
  const binInputRef = useRef(null);
  const bundleInputRef = useRef(null);

  const [targetBin, setTargetBin] = useState(null);
  const [targetBinInput, setTargetBinInput] = useState('');
  const [bundleInput, setBundleInput] = useState('');
  const [transferredItems, setTransferredItems] = useState([]);
  const [isTransferring, setIsTransferring] = useState(false);
  const [scanFeedback, setScanFeedback] = useState(null);
  const [errors, setErrors] = useState({});
  const [step, setStep] = useState('bin');

  const transferredSet = useMemo(() => new Set(transferredItems.map(i => i.bundle_code)), [transferredItems]);

  useEffect(() => {
    if (step === 'bin' && binInputRef.current) binInputRef.current.focus();
    else if (step === 'bundle' && bundleInputRef.current) bundleInputRef.current.focus();
  }, [step]);

  const showFeedback = (type, message) => {
    setScanFeedback({ type, message });
    setTimeout(() => setScanFeedback(null), 350);
  };

  const handleBinScan = async (value) => {
    if (!value?.trim()) {
      setErrors(e => ({ ...e, bin: 'Bin code is required' }));
      showFeedback('error', 'Bin Code Required');
      return;
    }
    try {
      const { data } = await apiClient.get(`${apiBase}/sfg/bin-transfer/validate-bin/${encodeURIComponent(value.trim())}`);
      if (!data.valid) {
        setErrors(e => ({ ...e, bin: data.message }));
        showFeedback('error', 'Invalid Bin');
        setTargetBinInput('');
        return;
      }
      setTargetBin(data);
      setTargetBinInput('');
      setErrors(e => ({ ...e, bin: null }));
      showFeedback('success', `Bin: ${value}`);
      setStep('bundle');
      toast.success(`Target bin ${value} validated - ${data.current_qty} bundles stored`);
    } catch (err) {
      setErrors(e => ({ ...e, bin: 'Failed to validate bin' }));
      showFeedback('error', 'Validation Error');
      setTargetBinInput('');
    }
  };

  const handleBundleScan = async (value) => {
    if (!value?.trim()) {
      setErrors(e => ({ ...e, bundle: 'Bundle code is required' }));
      showFeedback('error', 'Bundle Required');
      return;
    }
    if (transferredSet.has(value.trim())) {
      setErrors(e => ({ ...e, bundle: 'Bundle already transferred in this session' }));
      showFeedback('error', 'Already Transferred');
      setBundleInput('');
      return;
    }
    try {
      const { data: valData } = await apiClient.get(`${apiBase}/sfg/bin-transfer/validate-bundle/${encodeURIComponent(value.trim())}`);
      if (!valData.valid) {
        setErrors(e => ({ ...e, bundle: valData.message }));
        showFeedback('error', 'Invalid Bundle');
        setBundleInput('');
        return;
      }
      if (valData.current_bin === targetBin.bin_code) {
        setErrors(e => ({ ...e, bundle: `Bundle is already in bin ${targetBin.bin_code}` }));
        showFeedback('error', 'Already in Target');
        setBundleInput('');
        return;
      }

      setIsTransferring(true);
      const { data: txData } = await apiClient.post(`${apiBase}/sfg/bin-transfer/transfer`, {
        bundle_code: value.trim(),
        target_bin_code: targetBin.bin_code,
      });

      if (txData.success) {
        setTransferredItems(prev => [{
          bundle_code: txData.bundle_code,
          design_name: txData.design_name,
          shade: txData.shade,
          usn_count: txData.usn_count,
          source_bin: txData.source_bin,
          target_bin: txData.target_bin,
          timestamp: new Date().toISOString(),
        }, ...prev]);
        setTargetBin(prev => ({ ...prev, current_qty: txData.target_bin_qty }));
        setBundleInput('');
        setErrors(e => ({ ...e, bundle: null }));
        showFeedback('success', 'Transferred!');
        toast.success(`Bundle transferred from ${txData.source_bin} to ${targetBin.bin_code}`);
        setTimeout(() => bundleInputRef.current?.focus(), 100);
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to transfer bundle';
      setErrors(e => ({ ...e, bundle: msg }));
      showFeedback('error', 'Transfer Failed');
      toast.error(msg);
      setBundleInput('');
    } finally {
      setIsTransferring(false);
    }
  };

  const handleChangeBin = () => { setTargetBin(null); setStep('bin'); setTransferredItems([]); };
  const handleNewTransfer = () => { setTargetBin(null); setTargetBinInput(''); setBundleInput(''); setTransferredItems([]); setStep('bin'); };
  if (!sectionName) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#FFFBF5] p-6" data-testid={`persectionsfgbintobintransfer-unknown`}>
        <h2 className="text-lg font-bold">Unknown section</h2>
        <p className="text-sm text-gray-500 mt-1">No catalog entry for slug <code className="font-mono">{slug}</code>.</p>
      </div>
    );
  }


  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="sfg-bin-to-bin-transfer">
      <ScanFeedbackOverlay feedback={scanFeedback} />

      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate(`${apiBase}/sfg`)} className="text-[#F9FAFB] hover:bg-white/10" data-testid="sfg-bin-transfer-back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display flex items-center gap-2">
                <ArrowRightLeft className="w-6 h-6" />
                SFG Bin to Bin Transfer
              </h1>
              <p className="text-sm text-[#E8DCC8]">Transfer bundles between bins in SFG storage</p>
            </div>
          </div>
          {transferredItems.length > 0 && (
            <Button onClick={handleNewTransfer} variant="outline" className="bg-white/10 border-white/20 text-white hover:bg-white/20" data-testid="sfg-bin-transfer-new-btn">
              <RefreshCw className="w-4 h-4 mr-2" /> New Transfer
            </Button>
          )}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 md:px-6 py-6 space-y-6">
        {/* Step 1: Scan Target Bin */}
        {step === 'bin' && (
          <Card className="border-[#E5DFD0]">
            <CardHeader>
              <CardTitle className="text-[#1A1F2E] flex items-center gap-2">
                <span className="bg-[#2D5F4F] text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">1</span>
                Scan Target Bin
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600 mb-4">Scan or enter the bin code where you want to transfer bundles</p>
              <BarcodeInput
                ref={binInputRef}
                value={targetBinInput}
                onChange={setTargetBinInput}
                onScan={handleBinScan}
                placeholder="Scan target bin code (e.g., H01-A-01-L1-B01)"
                error={errors.bin}
                data-testid="sfg-bin-transfer-bin-input"
              />
            </CardContent>
          </Card>
        )}

        {/* Step 2: Scan Bundles */}
        {step === 'bundle' && targetBin && (
          <>
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="bg-blue-100 p-3 rounded-lg">
                      <MapPin className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-blue-600 font-medium">Target Bin</p>
                      <p className="text-xl font-bold text-blue-800" data-testid="sfg-bin-transfer-target-bin">{targetBin.bin_code}</p>
                      <p className="text-xs text-blue-600">{targetBin.location_path}</p>
                      {targetBin.existing_design && (
                        <p className="text-xs text-amber-600 mt-1 font-medium">
                          Contains: {targetBin.existing_design.design_name}/{targetBin.existing_design.shade}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-blue-600">Current Bundles</p>
                    <p className="text-2xl font-bold text-blue-800" data-testid="sfg-bin-transfer-current-qty">{targetBin.current_qty}</p>
                    <Button variant="ghost" size="sm" onClick={handleChangeBin} className="text-blue-600 hover:text-blue-700 mt-1" data-testid="sfg-bin-transfer-change-bin">
                      Change Bin
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-[#E5DFD0]">
              <CardHeader className="pb-3">
                <CardTitle className="text-[#1A1F2E] flex items-center gap-2">
                  <span className="bg-[#2D5F4F] text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">2</span>
                  Scan Bundles to Transfer
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-4">Scan bundle codes to move them to bin <strong>{targetBin.bin_code}</strong></p>
                <BarcodeInput
                  ref={bundleInputRef}
                  value={bundleInput}
                  onChange={setBundleInput}
                  onScan={handleBundleScan}
                  placeholder="Scan bundle code (e.g., BUN007338/25-26)"
                  error={errors.bundle}
                  disabled={isTransferring}
                  data-testid="sfg-bin-transfer-bundle-input"
                />
                {isTransferring && (
                  <div className="flex items-center gap-2 mt-2 text-sm text-gray-500">
                    <RefreshCw className="w-4 h-4 animate-spin" /> Transferring...
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="grid grid-cols-2 gap-4">
              <Card className="bg-green-50 border-green-200">
                <CardContent className="p-4 text-center">
                  <Check className="w-6 h-6 mx-auto text-green-600 mb-1" />
                  <p className="text-2xl font-bold text-green-800" data-testid="sfg-bin-transfer-count">{transferredItems.length}</p>
                  <p className="text-xs text-green-600">Bundles Transferred</p>
                </CardContent>
              </Card>
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="p-4 text-center">
                  <Package className="w-6 h-6 mx-auto text-blue-600 mb-1" />
                  <p className="text-2xl font-bold text-blue-800">{targetBin.current_qty}</p>
                  <p className="text-xs text-blue-600">Bundles in Target Bin</p>
                </CardContent>
              </Card>
            </div>

            {transferredItems.length > 0 && (
              <Card className="border-[#E5DFD0]">
                <CardHeader className="pb-3">
                  <CardTitle className="text-[#1A1F2E]" data-testid="sfg-bin-transfer-table-title">Transferred Bundles ({transferredItems.length})</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="rounded-md border overflow-x-auto max-h-[400px] overflow-y-auto">
                    <Table>
                      <TableHeader className="sticky top-0 bg-gray-50">
                        <TableRow>
                          <TableHead className="w-[50px]">#</TableHead>
                          <TableHead>Bundle Code</TableHead>
                          <TableHead>Design</TableHead>
                          <TableHead>Shade</TableHead>
                          <TableHead>Pieces</TableHead>
                          <TableHead>From Bin</TableHead>
                          <TableHead>To Bin</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {transferredItems.map((item, idx) => (
                          <TableRow key={item.bundle_code} className="bg-green-50/50">
                            <TableCell className="text-gray-500">{transferredItems.length - idx}</TableCell>
                            <TableCell className="font-mono font-medium" data-testid={`sfg-transfer-row-${idx}`}>{item.bundle_code}</TableCell>
                            <TableCell>{item.design_name || '-'}</TableCell>
                            <TableCell>{item.shade || '-'}</TableCell>
                            <TableCell>{item.usn_count || '-'}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="font-mono text-xs">{item.source_bin}</Badge>
                            </TableCell>
                            <TableCell>
                              <Badge className="bg-green-100 text-green-800 font-mono text-xs">{item.target_bin}</Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default PerSectionSFGBinToBinTransfer;
